import Vue from 'vue';
import Vuex from 'vuex';
import firebase from 'firebase';
import VuexPersistence from 'vuex-persist';
import UUID from 'uuid';
import _set from 'lodash/set';
import _orderBy from 'lodash/orderBy';
import VERSION from '../../version';
import gql from 'graphql-tag';
import { createProvider } from '../../vue-apollo';
import {toImage, toProject, toSiteLog} from '../transformers';

const CREATE_PROJECT = gql`
  mutation creataProject($companyId: String!, $projectId: String!, $name: String!) {
      createProject(
          company_id: $companyId,
          project_id: $projectId,
          name      : $name
      )
  }
`;

const ADD_PROJECT_IMAGE = gql`
    mutation addProjectImage($companyId: String!, $projectId: String!, $imageId: String!, $date: String!, $originalName: String!, $description: String!, $downloadUrl: String!) {
        addProjectImage (
            company_id   : $companyId,
            project_id   : $projectId,
            image_id     : $imageId,
            date         : $date,
            original_name: $originalName,
            description  : $description,
            download_url : $downloadUrl
        )
    }
`;

const UPDATE_PROJECT_IMAGE = gql`
    mutation updateProjectImage($companyId: String!, $projectId: String!, $imageId: String!, $date: String!, $description: String!) {
        updateProjectImage (
            company_id   : $companyId,
            project_id   : $projectId,
            image_id     : $imageId,
            date         : $date,
            description  : $description
        )
    }
`;

const DELETE_PROJECT_IMAGE = gql`
    mutation deleteProjectImage($companyId: String!, $projectId: String!, $imageId: String!) {
        deleteProjectImage (
            company_id   : $companyId,
            project_id   : $projectId,
            image_id     : $imageId
        )
    }
`;

const CREATE_SITELOG = gql`
  mutation createSiteLog($companyId: String!, $projectId: String!, $date: String!) {
    createSiteLog(
      company_id: $companyId,
      project_id: $projectId,
      date      : $date
    )
  }
`;

const UPDATE_SITELOG = gql`
    mutation updateSiteLogField($companyId: String!, $projectId: String!, $date: String!, $fieldPath: String!, $value: String!) {
        updateSiteLogField(
            company_id: $companyId,
            project_id: $projectId,
            date      : $date,
            fieldPath : $fieldPath,
            value     : $value
        )
    }
`;

const DELETE_SITELOG = gql`
    mutation deleteSiteLog($companyId: String!, $projectId: String!, $date: String!) {
        deleteSiteLog(
            company_id: $companyId,
            project_id: $projectId,
            date      : $date
        )
    }
`;

const ADD_MACHINE_TO_SITELOG = gql`
    mutation addMachineToSiteLog($companyId: String!, $projectId: String!, $date: String!, $machineId: String!, $type: String!, $description: String!, $count: Int!) {
        addMachineToSiteLog(
            company_id : $companyId,
            project_id : $projectId,
            date       : $date,
            machine_id : $machineId,
            type       : $type,
            description: $description,
            count      : $count
        )
    }
`;

const UPDATE_MACHINE_ON_SITELOG = gql`
    mutation updateMachineOnSiteLog($companyId: String!, $projectId: String!, $date: String!, $machineId: String!, $type: String!, $description: String, $count: Int) {
        updateMachineOnSiteLog(
            company_id : $companyId,
            project_id : $projectId,
            date       : $date,
            machine_id : $machineId,
            type       : $type,
            description: $description,
            count      : $count
        )
    }
`;

const REMOVE_MACHINE_FROM_SITELOG = gql`
    mutation removeMachineFromSiteLog($companyId: String!, $projectId: String!, $date: String!, $machineId: String!, $type: String!) {
        removeMachineFromSiteLog(
            company_id : $companyId,
            project_id : $projectId,
            date       : $date,
            machine_id : $machineId,
            type       : $type
        )
    }
`;

const ADD_ATTACHMENT_TO_SITELOG = gql`
    mutation addAttachmentToSitelog($companyId: String!, $projectId: String!, $date: String!, $attachmentId: String!, $originalName: String!, $downloadUrl: String!) {
        addAttachmentToSitelog(
            company_id    : $companyId,
            project_id    : $projectId,
            date          : $date,
            attachment_id : $attachmentId,
            original_name : $originalName,
            download_url  : $downloadUrl
        )
    }
`;

const apolloClient = createProvider().defaultClient;

const vuexLocal = new VuexPersistence({
  storage: window.localStorage
});

Vue.use(Vuex);

export default new Vuex.Store({
  strict: true,
  plugins: [vuexLocal.plugin],
  state: {
    version: VERSION,
    currentUser: {
      id: null,
      email: '',
      is_authenticated: false,
      companies: []
    },
    currentUserRoles: [],
    currentCompany: {
      id: null,
      name: ''
    },
    // List of companies user has access to
    companies: [],
    listOfProjects: [
      /** Example:
       * {
       *   id  : 1,
       *   name: ''
       * }
       */
    ],
    projects: [],
    uvodniListi: [],
    siteLogs: [],
    listOfSiteLogs: [
      /**
       * Example:
       * {
       *   project_id: 'uuid',
       *   sequence  : 1,
       *   date      : '2019-02-05'
       * }
       */
    ],
    siteLogImages: [
      /**
       * Example:
       * {
       *   project_id: projectId,
       *   image_id: imageId,
       *   date: imageDate,
       *   original_name: image.name,
       *   description: description,
       *   download_url: downloadUrl,
       *   thumbs: {
       *     // Let's make full image as thumbnail for now (as thumbnails are generated in background)
       *     256: [downloadUrl],
       *     1024: [downloadUrl]
       *   }
       * }
       */
    ],
    users: [
      /**
       * All users in account.
       *
       *
       * Example:
       *  {
       *    id: null,
       *    email: '',
       *    first_name: '',
       *    last_name: '',
       *    role: 'admin' | ...
       *  }
       */
    ]
  },
  getters: {
    currentUser(state) {
      return state.currentUser;
    },
    currentCompany(state) {
      return state.currentCompany;
    },
    companies(state) {
      return state.companies;
    },

    // ------------------------------------------------------
    //                   Project management
    // ------------------------------------------------------
    projectSummary: (state) => (projectId) => {
      return state
        .listOfProjects
        .find(p => p.project_id == projectId)
      ;
    },
    possibleProjectLeads: (state, getters) => (projectId) => {
      return state
        .users
        .filter(u => u.role === 'project_lead')
      ;
    },
    projectLeads: (state, getters) => (projectId) => {
      const projectLeads = getters.projectSummary(projectId).project_leads || [];

      return state
        .users
        .filter(u => projectLeads.includes(u.id) || u.role === 'admin')
      ;
    },
    possibleProjectReviewers: (state) => (projectId) => {
      return state
        .users
        .filter(u => u.role === 'reviewer')
      ;
    },
    projectReviewers: (state, getters) => (projectId) => {
      const projectReviewers = getters.projectSummary(projectId).project_reviewers || [];

      return state
        .users
        .filter(u => projectReviewers.includes(u.id))
      ;
    },
    uvodniList: (state) => (projectId) => {
      return state.uvodniListi.find(p => p.project_id == projectId);
    },
    uvodniListSection: (state) => (projectId, sectionNumber) => {
      const project = state.projects.find(p => p.project_id === projectId);

      return project
        .uvodni_list
        .sections[sectionNumber]
      ;
    },
    seznamDnevnihListov: (state) => (projectId) => {
      return _orderBy(
        state.listOfSiteLogs.filter(l => l.project_id == projectId),
        ['date'],
        ['asc']
      );
    },
    dnevniList: (state) => (projectId, date) => {
      return state.siteLogs.find(d => d.project_id === projectId && d.date === date);
    },
    siteLogImages: (state) => (projectId) => {
      return state
        .siteLogImages
        .filter((i) => i && i.project_id === projectId && 'undefined' !== typeof i.date)
        ;
    },
    previousSiteLogDate: (state) => (projectId, beforeDate) => {
      const previousSiteLog = _orderBy(
        state.listOfSiteLogs.filter(l => l.project_id === projectId && l.date < beforeDate),
        ['date'],
        ['desc']
      );

      return previousSiteLog.length > 0 ? previousSiteLog[0].date : null;
    },
    nextSiteLogDate: (state) => (projectId, afterDate) => {
      const nextSiteLog = _orderBy(
        state.listOfSiteLogs.filter(l => l.project_id === projectId && l.date > afterDate),
        ['date'],
        ['asc']
      );

      return nextSiteLog.length > 0 ? nextSiteLog[0].date : null;
    },
  },
  mutations: {
    // ------------------------------------------------------
    //                     User management
    // ------------------------------------------------------
    userLoggedIn(state, info) {
      state.currentUser          = info.user;
      state.currentUser.role     = info.roles.find(r => r.company_id === info.company.company_id).role_type;
      state.currentUserRoles     = info.roles;
      state.currentCompany       = info.company;
      state.companies            = info.companies;
    },
    usersListUpdated(state, users) {
      state.users = users;
    },
    userRoleChanged(state, { userId, roleType }) {
      const user = state.users.find(u => u.id === userId);
      user.role  = roleType;
    },
    userRemovedFromCompany(state, { userId }) {
      state.users = state.users.filter(u => u.id !== userId);
    },
    companySwitched(state, { companyId }) {
      state.currentCompany   = state.companies.find(c => c.company_id === companyId);
      state.currentUser.role = state.currentUserRoles.find(r => r.company_id === companyId).role_type;
      state.projects       = [];
      state.listOfProjects = [];
      state.users          = [];
    },

    // ------------------------------------------------------
    //                    Gradbeni dnevnik
    // ------------------------------------------------------
    projectCreated(state, project) {
      // Clear existing records if already exists
      state.listOfProjects = state.listOfProjects.filter(p => p.project_id != project.project_id);

      state.listOfProjects.push({ project_id: project.project_id, name: project.name, project_leads: project.project_leads, project_reviewers: project.project_reviewers, archived: !!project.archived });

      state.projects = state.projects.filter(p => p.project_id !== project.project_id);
      state.projects.push(project);
    },
    projectFetched(state, project) {
      state.projects = state.projects.filter(p => p.project_id !== project.project_id);

      state.projects.push(project);
    },
    projectNameChanged(state, { projectId, name }) {
      state.listOfProjects.find(p => p.project_id === projectId).name = name;
    },
    projectDeleted(state, projectId) {
      state.listOfProjects = state.listOfProjects.filter(p => p.project_id != projectId);
      state.uvodniListi    = state.uvodniListi.filter(p => p.project_id != projectId);
    },
    projectLeadsChanged(state, { projectId, userIds }) {
      state
        .listOfProjects
        .find(p => p.project_id === projectId)
        .project_leads = userIds
      ;
    },
    projectArchived(state, projectId) {
      state
        .listOfProjects
        .find(p => p.project_id === projectId)
        .archived = true
      ;
    },
    projectUnarchived(state, projectId) {
      state
        .listOfProjects
        .find(p => p.project_id === projectId)
        .archived = false
      ;
    },
    projectReviewersChanged(state, { projectId, userIds }) {
      state
        .listOfProjects
        .find(p => p.project_id === projectId)
        .project_reviewers = userIds
      ;
    },
    introductoryPageSectionUpdated(state, { projectId, sectionNumber, fieldPath, value }) {
      state
        .projects
        .find(p => p.project_id === projectId)
        .uvodni_list
        .sections[sectionNumber][fieldPath] = value;
      ;
    },
    siteLogCreated(state, siteLog) {
      state.listOfSiteLogs = state.listOfSiteLogs.filter(l => !(l.date === siteLog.date && l.project_id === siteLog.project_id));

      state.listOfSiteLogs.push({
        project_id: siteLog.project_id,
        date      : siteLog.date
      });
    },
    siteLogFetched(state, siteLog) {
      state.siteLogs = state.siteLogs.filter(l => !(l.date === siteLog.date && l.project_id === siteLog.project_id));

      state.siteLogs.push(siteLog);
    },
    siteLogDeleted(state, { projectId, date }) {
      state.siteLogs = state
        .siteLogs
        .filter(s => !(s.project_id === projectId && s.date === date))
      ;

      state.listOfSiteLogs = state
        .listOfSiteLogs
        .filter(s => !(s.project_id === projectId && s.date === date))
      ;
    },
    siteLogFieldUpdated(state, { projectId, date, fieldPath, value }) {
      const siteLog = state.siteLogs.find(l => l.project_id === projectId && l.date === date);

      _set(siteLog, fieldPath, value);
    },
    machineAddedToSiteLog(state, { projectId, date, id, type, description, count }) {
      const siteLog = state.siteLogs.find(l => l.project_id == projectId && l.date == date);
      const machine = siteLog.stroji[type].find(m => m.id == id);

      if (machine) {
        machine.description = description;
        machine.count       = count;
      } else {
        siteLog.stroji[type].push({id: id, description: description, count: count})
      }
    },
    machineRemovedFromSiteLog(state, { projectId, date, id, type }) {
      const siteLog = state.siteLogs.find(l => l.project_id == projectId && l.date == date);

      siteLog.stroji[type] = siteLog.stroji[type].filter(m => m.id != id);
    },
    machineDescriptionUpdated(state, { projectId, date, id, type, description }) {
      const siteLog = state.siteLogs.find(l => l.project_id == projectId && l.date == date);
      const machine = siteLog.stroji[type].find(m => m.id == id);

      if (machine) {
        machine.description = description;
      }
    },
    machineCountUpdated(state, { projectId, date, id, type, count }) {
      const siteLog = state.siteLogs.find(l => l.project_id == projectId && l.date == date);
      const machine = siteLog.stroji[type].find(m => m.id == id);

      if (machine) {
        machine.count = count;
      }
    },
    siteLogFileAttached(state, { projectId, date, attachment }) {
      const siteLog = state.siteLogs.find(l => l.project_id == projectId && l.date == date);

      siteLog.attachments = siteLog.attachments.filter(a => a && a.attachment_id !== attachment.attachment_id);
      siteLog.attachments.push(attachment);
    },
    siteLogImageCreated(state, image) {
      state.siteLogImages = state.siteLogImages.filter(i => i && i.image_id !== image.image_id);
      state.siteLogImages.push(image);
    },
    siteLogImageUpdated(state, updatedImageData) {
      const image = state.siteLogImages.find(i => i && i.image_id === updatedImageData.image_id);

      if (image) {
        image.date        = updatedImageData.date;
        image.description = updatedImageData.description;
      }
    },
    siteLogImageDeleted(state, imageId) {
      state.siteLogImages = state.siteLogImages.filter(i => i && i.image_id !== imageId);
    }
  },
  actions: {
    // ------------------------------------------------------
    //                     User management
    // ------------------------------------------------------

    /**
     * Logs in user with the default company
     *
     * @param commit
     * @param {string} email
     * @param {string} password
     * @param {boolean} emailLogin
     * @param {string} windowLocationHref
     * @param {string} companyId
     * @returns {Promise<void>}
     */
    async userlogin({ commit }, { email, password, emailLogin, windowLocationHref, companyId }) {
      let credentials;

      email = email.toLowerCase();

      if (emailLogin) {
        credentials = await firebase
          .auth()
          .signInWithEmailLink(email, windowLocationHref)
        ;
      } else {
        credentials = await firebase
          .auth()
          .signInWithEmailAndPassword(email, password)
        ;
      }

      const db        = firebase.firestore();
      const userRoles = (await db
        .collection('user_roles')
        .where('user_id', '==', credentials.user.uid)
        .get())
        .docs
        .map(d => d.data())
      ;

      let defaultCompany = false;

      const companyIdsUserHasAccessTo = new Set( userRoles.map(r => r.company_id) );
      const companiesUserHasAccessTo  = (await db
        .collection('companies')
        .where('company_id', 'in', Array.from(companyIdsUserHasAccessTo))
        .get())
        .docs
        .map(d => d.data())
      ;

      if (userRoles.length > 0) {
        defaultCompany =
          companiesUserHasAccessTo.find(c => c.company_id === companyId) ||
          companiesUserHasAccessTo.find(c => c.company_id === userRoles[0].company_id)
        ;
      }

      commit('userLoggedIn', {
        user: {
          id:    credentials.user.uid,
          email: credentials.user.email.toLowerCase(),
          is_authenticated: true,
        },
        roles  : userRoles.map(r => { return { role_type: r.role_type, company_id: r.company_id } }),
        company: defaultCompany
          ? { company_id: defaultCompany.company_id, name: defaultCompany.name }
          : false,
        companies: companiesUserHasAccessTo
      });
    },

    async switchCompany({ commit, dispatch }, toCompanyId) {
      commit('companySwitched', {
        companyId: toCompanyId
      });

      dispatch('fetchProjects');
    },

    async migrationTo110Action({ state, commit }) {
      const db        = firebase.firestore();
      const userRoles = (await db
        .collection('user_roles')
        .where('user_id', '==', state.currentUser.id)
        .get())
        .docs
        .map(d => d.data())
      ;

      let defaultCompany = false;

      const companyIdsUserHasAccessTo = new Set( userRoles.map(r => r.company_id) );
      const companiesUserHasAccessTo  = (await db
        .collection('companies')
        .where('company_id', 'in', Array.from(companyIdsUserHasAccessTo))
        .get())
        .docs
        .map(d => d.data())
      ;

      if (userRoles.length > 0) {
        defaultCompany = companiesUserHasAccessTo.find(c => c.company_id === userRoles[0].company_id);
      }

      commit('userLoggedIn', {
        user: state.currentUser,
        roles  : userRoles.map(r => { return { role_type: r.role_type, company_id: r.company_id } }),
        company: defaultCompany
          ? { company_id: defaultCompany.company_id, name: defaultCompany.name }
          : false,
        companies: companiesUserHasAccessTo
      });
    },

    /**
     * Creates:
     *  - new user
     *  - new company
     *  - creates user role
     *
     * @param commit
     * @param {string} firstName
     * @param {string} lastName
     * @param {string} email
     * @param {string} password
     * @param {string} companyName
     * @returns {Promise<void>}
     */
    async registerNewUser({ commit }, { firstName, lastName, email, password, companyName }) {
      email           = email.toLowerCase();
      const db        = firebase.firestore();
      const companyId = UUID.v1();

      const credentials = await firebase.auth().createUserWithEmailAndPassword(email, password);

      await db.collection('companies').add({
        company_id: companyId,
        name      : companyName
      });

      await db.collection('user_roles').add({
        user_id   : credentials.user.uid,
        company_id: companyId,
        role_type : 'admin'
      });

      await db.collection('user_profiles').doc(credentials.user.uid).set({
        user_id   : credentials.user.uid,
        first_name: firstName,
        last_name : lastName,
        email     : email
      });

      commit('userLoggedIn', {
        user: {
          id:    credentials.user.uid,
          email: credentials.user.email.toLowerCase(),
          is_authenticated: true,
          profile: {
            first_name: firstName,
            last_name : lastName
          }
        },
        roles: [
          { role_type: 'admin', company_id: companyId},
        ],
        company: {
          company_id: companyId,
          name      : companyName
        },
        companies: [
          {
            company_id: companyId,
            name      : companyName
          }
        ]
      });
    },

    async inviteUser({ commit, state }, { companyId, firstName, lastName, email, roleType }) {
      email = email.toLowerCase();

      const db = firebase.firestore();

      const inviterId       = state.currentUser.id;
      const acceptInviteUrl = `${window.location.protocol}//${window.location.hostname}/login?email=${email}`;

      try {
        const invitedUser = await firebase.auth().createUserWithEmailAndPassword(email, btoa(Math.random()));
        await db.collection('user_roles').add({
          user_id   : invitedUser.user.uid,
          company_id: companyId,
          inviter_id: inviterId,
          role_type : roleType
        });

        await db.collection('user_profiles').doc(invitedUser.user.uid).set({
          user_id   : invitedUser.user.uid,
          first_name: firstName,
          last_name : lastName,
          email     : email
        });

        await db.collection('invites').doc(invitedUser.user.uid).set({
          user_id   : invitedUser.user.uid,
          email     : email,
          company_id: companyId,
          inviter_id: inviterId,
          role_type : roleType,
          url       : acceptInviteUrl
        });
      } catch (e) {
        if ('auth/email-already-in-use' === e.code) {
          const existingUserProfile = (await db
            .collection('user_profiles')
            .where('email', '==', email)
            .limit(1)
            .get())
            .docs[0]
            .data()
          ;

          await db.collection('user_roles').add({
            user_id   : existingUserProfile.user_id,
            company_id: companyId,
            inviter_id: inviterId,
            role_type : roleType,
            invited   : true
          });

          const loginUrl = acceptInviteUrl + '&company_id=' + companyId;

          await db.collection('invites').add({
            email     : email,
            company_id: companyId,
            inviter_id: inviterId,
            role_type : roleType,
            url       : loginUrl
          });
        }
      }
    },

    /**
     *
     * @return {Promise<User[]>}
     */
    async fetchUsers({ commit, state }) {
      const db        = firebase.firestore();
      const companyId = state.currentCompany.company_id;

      const users = await Promise.all((await db
        .collection('user_roles')
        .where('company_id', '==', companyId)
        .get())
        .docs
        .map(async (roleDoc) => {
          const role = roleDoc.data();
          const user = {
            // Schema
            id: role.user_id,
            email: '',
            first_name: '',
            last_name: '',
            role: role.role_type
          };

          // const userInfo = await firebase.auth().getUser(user.id);
          const userProfile = (await db
            .collection('user_profiles')
            .doc(user.id)
            .get())
            .data()
          ;

          user.email      = userProfile && userProfile.email.toLowerCase() || '?';
          user.first_name = userProfile && userProfile.first_name || '';
          user.last_name  = userProfile && userProfile.last_name || '';

          return user;
        })
      );

      commit('usersListUpdated', users);
    },

    async changeUserRole({ state, commit }, { userId, roleType }) {
      const companyId     = state.currentCompany.company_id;
      const db            = firebase.firestore();

      const existingRoles = await db
        .collection('user_roles')
        .where('company_id', '==', companyId)
        .where('user_id', '==', userId)
        .get()
      ;

      const deleteBatch = db.batch();
      existingRoles.forEach((snapshot) => {
        deleteBatch.delete(snapshot.ref);
      });

      await deleteBatch.commit();

      await db.collection('user_roles').add({
        user_id   : userId,
        company_id: companyId,
        role_type : roleType
      });

      commit('userRoleChanged', { companyId, userId, roleType });
    },

    async setProjectLeadAccess({ state, commit }, { userId, projectIds }) {
      const db          = firebase.firestore();
      const batchUpdate = db.batch();
      const companyId   = state.currentCompany.company_id;

      const projects = await db
        .collection('projects')
        .where('company_id', '==', companyId)
        .get()
      ;

      projects.forEach(doc => {
        const project          = doc.data();
        const projectLeads     = new Set(project.project_leads || []);
        const projectReviewers = new Set(project.project_reviewers || []);

        if (-1 === projectIds.indexOf(project.project_id)) {
          projectLeads.delete(userId);
        } else {
          projectLeads.add(userId);
          projectReviewers.delete(userId);
        }

        batchUpdate.update(doc.ref, {
          project_reviewers: Array.from(projectReviewers),
          project_leads    : Array.from(projectLeads)
        });
      });

      await batchUpdate.commit();
    },

    async setProjectReviewerAccess({ state, commit }, { userId, projectIds }) {
      const db          = firebase.firestore();
      const batchUpdate = db.batch();
      const companyId   = state.currentCompany.company_id;

      const projects = await db
        .collection('projects')
        .where('company_id', '==', companyId)
        .get()
      ;

      projects.forEach(doc => {
        const project          = doc.data();
        const projectLeads     = new Set(project.project_leads || []);
        const projectReviewers = new Set(project.project_reviewers || []);

        if (-1 === projectIds.indexOf(project.project_id)) {
          projectReviewers.delete(userId);
        } else {
          projectReviewers.add(userId);
          projectLeads.delete(userId);
        }

        batchUpdate.update(doc.ref, {
          project_reviewers: Array.from(projectReviewers),
          project_leads    : Array.from(projectLeads)
        });
      });

      await batchUpdate.commit();
    },

    async removeUserFromCompany({ state, commit }, { userId }) {
      const companyId = state.currentCompany.company_id;
      const db        = firebase.firestore();

      const existingRoles = await db
        .collection('user_roles')
        .where('company_id', '==', companyId)
        .where('user_id', '==', userId)
        .get()
      ;

      const deleteBatch = db.batch();
      existingRoles.forEach((snapshot) => {
        deleteBatch.delete(snapshot.ref);
      });

      await deleteBatch.commit();

      commit('userRemovedFromCompany', { companyId, userId });
    },

    // ------------------------------------------------------
    //                    Gradbeni dnevnik
    // ------------------------------------------------------

    async createNewProject({ commit, state }, project) {
      const projectId = project.projectId || UUID.v1();

      const projectDoc = {
        project_id : projectId,
        name       : project.name,
        company_id : state.currentCompany.company_id,
        uvodni_list: {
          sections: {
            1: {
              section_number: 1,
              vrsta_objekta: ' ',
              kraj_gradnje: '',
              kraj_za_vreme: '',
              vrsta_gradnje: ''
            },
            2: {
              section_number: 2,
              naziv: ''
            },
            3: {
              section_number: 3,
              naziv: '',
              odgovorni: '',
              vodja_gradbisca: '',
              vodja_posameznih_del: ''
            },
            4: {
              section_number: 4,
              stevilka_in_datum: ''
            },
            5: {
              section_number: 5,
              izdajatelj: '',
              stevilka_in_datum: ''
            },
            6: {
              section_number: 6,
              stevilka_in_datum: ''
            },
            7: {
              section_number: 7,
              naziv: '',
              odgovorna_oseba: '',
              odgovorna_oseba_posameznih_del: ''
            },
            8: {
              section_number: 8,
              naziv: '',
              arheolog: ''
            },
            9: {
              section_number: 9,
              odgovorni_gradbenega_dnevnika: '',
              odgovorni_obracunskih_mer: ''
            },
            10: {
              section_number: 10,
              stevilka_varnostnega_nacrta: '',
              izvajalec: '',
              koordinator_za_varnost: ''
            },
            11: {
              section_number: 11,
              datum: '',
              podpisi: ''
            }
          }
        }
      };

      commit('projectCreated', projectDoc);

      await apolloClient.mutate({
        mutation: CREATE_PROJECT,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          name     : project.name
        }
      });
    },
    async changeProjectName({ commit, state }, { projectId, name }) {
      apolloClient.mutate({
        mutation: gql`mutation ($companyId: String!, $projectId: String!, $name: String!) {
            renameProject(
                company_id: $companyId,
                project_id: $projectId,
                name: $name
            )
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          name     : name
        }
      });

      commit('projectNameChanged', { projectId, name });
    },
    async deleteProject({ commit, state }, projectId) {
      apolloClient.mutate({
        mutation: gql`mutation ($companyId: String!, $projectId: String!) {
            deleteProject(
                company_id: $companyId,
                project_id: $projectId
            )
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId
        }
      });

      commit('projectDeleted', projectId);
    },
    async archiveProject({ commit, state }, projectId) {
      apolloClient.mutate({
        mutation: gql`mutation ($companyId: String!, $projectId: String!) {
            archiveProject(
                company_id: $companyId
                project_id: $projectId
            )
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId
        }
      });

      commit('projectArchived', projectId);
    },

    async unarchiveProject({ commit, state }, projectId) {
      apolloClient.mutate({
        mutation: gql`mutation ($companyId: String!, $projectId: String!) {
            unarchiveProject(
                company_id: $companyId
                project_id: $projectId
            )
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId
        }
      });

      commit('projectUnarchived', projectId);
    },

    async setProjectLeads({ commit, state }, { projectId, userIds }) {
      const db = firebase.firestore();

      await db
        .collection('projects')
        .doc(projectId)
        .update({
          project_leads:  userIds
        })
      ;

      commit('projectLeadsChanged', { projectId, userIds });
    },

    async setProjectReviewers({ commit, state }, { projectId, userIds }) {
      const db = firebase.firestore();

      await db
        .collection('projects')
        .doc(projectId)
        .update({
          project_reviewers:  userIds
        })
      ;

      commit('projectReviewersChanged', { projectId, userIds });
    },

    async addProjectLead({ dispatch, state, getters }, { projectId, userId }) {
      const projectLeads = new Set(
        getters
          .projectLeads(projectId)
          .map(u => u.id)
      );

      projectLeads.add(userId);

      dispatch('setProjectLeads', { projectId: projectId, userIds: Array.from(projectLeads) });
    },

    async addProjectReviewer({ dispatch, state, getters }, { projectId, userId }) {
      const projectLeads = new Set(
        getters
          .projectLeads(projectId)
          .map(u => u.id)
      );

      const projectReviewers = new Set(
        getters
          .projectReviewers(projectId)
          .map(u => u.id)
      );

      projectLeads.delete(userId);
      projectReviewers.add(userId);

      dispatch('setProjectLeads', { projectId: projectId, userIds: Array.from(projectLeads) });
      dispatch('setProjectReviewers', { projectId: projectId, userIds: Array.from(projectReviewers) });
    },

    /**
     * Creates new site log.
     *
     * @param commit
     * @param state
     * @param siteLogDetails
     * @returns {Promise<void>}
     */
    async createNewSiteLog({ commit, state }, siteLogDetails) {
      await apolloClient.mutate({
        mutation: CREATE_SITELOG,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: siteLogDetails.projectId,
          date     : siteLogDetails.date
        }
      });

      commit('siteLogCreated', { project_id: siteLogDetails.projectId, date: siteLogDetails.date });
    },
    async deleteSiteLog({ commit, state }, { projectId, date }) {
      commit('siteLogDeleted', { projectId, date });

      await apolloClient.mutate({
        mutation: DELETE_SITELOG,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          date     : date
        }
      });
    },
    async fetchProjects({ commit, state }, archived) {
      const results = await apolloClient.query({
        query: gql`query ($company_id: String!, $archived: Boolean!) {
            projects(company_id: $company_id, archived: $archived) {
                project_id
                name
                archived
            }
        }`,
        variables: {
          company_id: state.currentCompany.company_id,
          archived: !!archived
        }
      });

      results.data.projects.forEach(p => commit('projectCreated', p));
    },

    async fetchProject({ commit, state }, projectId) {
      const results = await apolloClient.query({
        query: gql`query ($company_id: String!, $project_id: String!) {
            project(company_id: $company_id, project_id: $project_id) {
                project_id
                name
                project_leads
                recital {
                    building {
                        section_number
                        kraj_gradnje
                        kraj_za_vreme
                        vrsta_gradnje
                        vrsta_objekta
                    },
                    investor {
                        section_number
                        naziv
                    }
                    contractor {
                        section_number
                        naziv
                        odgovorni
                        vodja_gradbisca
                        vodja_posameznih_del
                    }
                    project_for_building_permit {
                        section_number
                        stevilka_in_datum
                    }
                    building_permit {
                        section_number
                        izdajatelj
                        stevilka_in_datum
                    }
                    execution_project {
                        section_number
                        stevilka_in_datum
                    }
                    supervisor {
                        section_number
                        naziv
                        odgovorna_oseba
                        odgovorna_oseba_posameznih_del
                    }
                    archeology {
                        section_number
                        arheolog
                        naziv
                    }
                    introductions {
                        section_number
                        datum
                        podpisi
                    }
                    responsibilities {
                        section_number
                        odgovorni_gradbenega_dnevnika
                        odgovorni_obracunskih_mer
                    }
                    documentation {
                        section_number
                        izvajalec
                        koordinator_za_varnost
                        stevilka_varnostnega_nacrta
                    }
                }
            }
        }`,
        variables: {
          company_id: state.currentCompany.company_id,
          project_id: projectId
        }
      });

      commit('projectFetched', toProject(results.data.project));
    },

    /**
     * Fetches all site logs for a project
     *
     * @param commit
     * @param state
     * @param projectId
     * @returns {Promise<void>}
     */
    async fetchSiteLogs({ commit, state }, projectId) {
      const results = await apolloClient.query({
        query: gql`query ($companyId: String!, $projectId: String!) {
            siteLogs(company_id: $companyId, project_id: $projectId) {
                date
                sequence
            }
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId
        }
      });

      results.data.siteLogs.forEach(s => commit('siteLogCreated', { project_id: projectId, ...s }));
    },

    async fetchSiteLog({ commit, state }, { projectId, date }) {
      const results = await apolloClient.query({
        query: gql`query ($companyId: String!, $projectId: String!, $date: String!) {
          siteLog(company_id: $companyId, project_id: $projectId, date: $date) {
            company_id
            project_id
            date
            delavci {
              drugi {
                izvajalci,
                kooperanti
                najeti
              }
              gradbena_dela {
                izvajalci,
                kooperanti
                najeti
              }
              instalaterji {
                izvajalci,
                kooperanti
                najeti
              }
              obrtniki {
                izvajalci,
                kooperanti
                najeti
              }
              vodstvo {
                izvajalci,
                kooperanti
                najeti
              }
            }
            delovni_cas {
              od
              do
            }
            dopolnilni_del
            nadzorni
            odgovorni
            opombe_ali_odgovori_izvajalca_del
            opombe_in_zahteve_inspekcijskih_sluzb
            opombe_in_zahteve_odgovornega_vodje
            opombe_in_zahteve_projektantov
            opombe_in_zahteve_soglasodajalcev
            sequence
            sestavil
            signature_nadzorni
            signature_sestavil
            signature_odgovorni
            sporocila_narocniku
            stroji {
              drugi {
                id
                description
                count
              }
              izvajalec {
                id
                description
                count
              }
            }
            vremenske_razmere {
              first {
                drugi_pogoji
                hitrost_vetra_ms
                merjeno_ob
                padavine_mm
                temperatura
                vodostaj_cm
                vreme
              }
              second {
                drugi_pogoji
                hitrost_vetra_ms
                merjeno_ob
                padavine_mm
                temperatura
                vodostaj_cm
                vreme
              }
              third {
                drugi_pogoji
                hitrost_vetra_ms
                merjeno_ob
                padavine_mm
                temperatura
                vodostaj_cm
                vreme
              }
            }
            attachments {
              attachment_id
              original_name
              download_url
            }
          }
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          date     : date
        }
      });

      commit('siteLogFetched', toSiteLog(results.data.siteLog));
    },

    async fetchSiteLogImages({ commit, state }, projectId) {
      const results = await apolloClient.query({
        query: gql`query images($companyId: String!, $projectId: String!) {
          images(company_id: $companyId, project_id: $projectId) {
            image_id
            project_id
            download_url
            original_name
            date
            description
            thumbs {
              width
              url
            }  
          }
        }`,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId
        }
      });

      results.data.images.forEach(i => commit('siteLogImageCreated', toImage(i)));
    },

    async updateIntroductoryPageSection({ commit, state }, { value, projectId, fieldPath, sectionNumber }) {
      await apolloClient.mutate({
        mutation: gql`mutation ($companyId: String!, $projectId: String!, $sectionNumber: Int!, $fieldPath: String!, $value: String!) {
            updateProjectRecital(
                company_id: $companyId,
                project_id: $projectId,
                section_number: $sectionNumber,
                field_path: $fieldPath,
                value: $value
            )
        }`,
        variables: {
          companyId    : state.currentCompany.company_id,
          projectId    : projectId,
          sectionNumber: sectionNumber,
          fieldPath    : fieldPath,
          value        : value
        }
      });
    },

    async updateSiteLogField({ commit, state }, { projectId, date, fieldPath, value }) {
      commit('siteLogFieldUpdated', { projectId, date, fieldPath, value });

      await apolloClient.mutate({
        mutation: UPDATE_SITELOG,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          date     : date,
          fieldPath: fieldPath,
          value    : value
        }
      });
    },

    async addMachineToSiteLog({ commit, state }, { projectId, date, type, description, count }) {
      const id = UUID.v1();

      commit('machineAddedToSiteLog', { projectId, date, id, type, description, count });

      await apolloClient.mutate({
        mutation: ADD_MACHINE_TO_SITELOG,
        variables: {
          companyId  : state.currentCompany.company_id,
          projectId  : projectId,
          date       : date,
          machineId  : id,
          type       : type,
          description: description ? description : '',
          count      : count
        }
      });
    },

    async removeMachineFromSiteLog({ commit, state }, { projectId, date, id, type }) {
      commit('machineRemovedFromSiteLog', { projectId, date, id, type });

      await apolloClient.mutate({
        mutation: REMOVE_MACHINE_FROM_SITELOG,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          date     : date,
          machineId: id,
          type     : type
        }
      });
    },

    async updateMachineOnSiteLog({ commit, state }, { projectId, date, id, type, description, count }) {
      const siteLog         = state.siteLogs.find(l => l.project_id == projectId && l.date == date);
      const machineCategory = siteLog.stroji[type];
      const existingMachine = Object.assign({}, machineCategory.find(m => m.id == id));

      if (existingMachine) {

        if (description) {
          commit('machineDescriptionUpdated', { projectId, date, id, type, description });
          existingMachine.description = description;
        }

        if (count) {
          commit('machineCountUpdated', { projectId, date, id, type, count });
          existingMachine.count = count;
        }

        await apolloClient.mutate({
          mutation: UPDATE_MACHINE_ON_SITELOG,
          variables: {
            companyId  : state.currentCompany.company_id,
            projectId  : projectId,
            date       : date,
            machineId  : id,
            type       : type,
            description: description ? description : null,
            count      : count ? parseInt(count) : null
          }
        });
      }
    },

    /**
     *
     * @param commit
     * @param state
     * @param projectId
     * @param {string} image     Base64 encoded image
     * @param {string} imageName
     * @param imageDate
     * @param description
     * @returns {Promise<void>}
     */
    async uploadSiteLogImage({ commit, state }, { projectId, image, imageName, imageDate, description }) {
      const storage = firebase.storage();

      const imageId   = UUID.v1();
      const imagePath = `images/${state.currentCompany.company_id}/${projectId}/${imageId}.${imageName.split('.').pop()}`;

      const metadata = {
        name: imageName,
        customMetadata: {
          image_id     : imageId,
          company_id   : state.currentCompany.company_id,
          project_id   : projectId,
          date         : imageDate,
          original_name: imageName,
          description  : description
        }
      };

      const uploadedImage = await storage
        .ref(imagePath)
        .put(image, metadata)
      ;

      const downloadUrl = await uploadedImage.ref.getDownloadURL();

      const imageData = {
        project_id   : projectId,
        image_id     : imageId,
        date         : imageDate,
        original_name: imageName,
        description  : description,
        download_url : downloadUrl,
        thumbs: {
          // Let's make full image as thumbnail for now (as thumbnails are generated in background)
          256 : [downloadUrl],
          1024: [downloadUrl]
        }
      };

      commit('siteLogImageCreated', imageData);

      await apolloClient.mutate({
        mutation: ADD_PROJECT_IMAGE,
        variables: {
          companyId   : state.currentCompany.company_id,
          projectId   : projectId,
          imageId     : imageId,
          date        : imageDate,
          originalName: imageName,
          description : description || '',
          downloadUrl : downloadUrl
        }
      });
    },

    /**
     * Updates date & description of an image
     *
     * @param commit
     * @param state
     * @param projectId
     * @param imageId
     * @param date
     * @param description
     * @returns {Promise<void>}
     */
    async updateSiteLogImage({ commit, state }, { projectId, imageId, date, description }) {
      commit('siteLogImageUpdated', {
        image_id   : imageId,
        date       : date,
        description: description
      });

      await apolloClient.mutate({
        mutation: UPDATE_PROJECT_IMAGE,
        variables: {
          companyId   : state.currentCompany.company_id,
          projectId   : projectId,
          imageId     : imageId,
          description : description || '',
          date        : date
        }
      });
    },

    /**
     *
     * @param commit
     * @param state
     * @param projectId
     * @param imageId
     * @returns {Promise<void>}
     */
    async deleteSiteLogImage({ commit, state }, { projectId, imageId }) {
      commit('siteLogImageDeleted', imageId);

      await apolloClient.mutate({
        mutation: DELETE_PROJECT_IMAGE,
        variables: {
          companyId: state.currentCompany.company_id,
          projectId: projectId,
          imageId  : imageId
        }
      });
    },

    /**
     *
     * @param commit
     * @param state
     * @param {String} projectId
     * @param {String} date
     * @param {File} attachment
     * @returns {Promise<void>}
     */
    async uploadSiteLogAttachment({ commit, state }, { projectId, date, attachment }) {
      const storage = firebase.storage();

      const attachmentId   = UUID.v1();
      const attachmentPath = `images/${state.currentCompany.company_id}/${projectId}/${attachmentId}.${attachment.name.split('.').pop()}`;

      const metadata = {
        name: attachment.name,
        customMetadata: {
          image_id     : attachmentId,
          company_id   : state.currentCompany.company_id,
          project_id   : projectId,
          original_name: attachment.name,
        }
      };

      const uploadedAttachment = await storage
        .ref(attachmentPath)
        .put(attachment, metadata)
      ;

      const downloadUrl   = await uploadedAttachment.ref.getDownloadURL();
      const attachmentDoc = {
        attachment_id: attachmentId,
        original_name: attachment.name,
        download_url : downloadUrl,
      };

      commit('siteLogFileAttached', {
        projectId : projectId,
        date      : date,
        attachment: attachmentDoc
      });

      await apolloClient.mutate({
        mutation: ADD_ATTACHMENT_TO_SITELOG,
        variables: {
          companyId   : state.currentCompany.company_id,
          projectId   : projectId,
          date        : date,
          attachmentId: attachmentId,
          originalName: attachment.name,
          downloadUrl : downloadUrl
        }
      });
    },
  },
});
